import { createSlice } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer/dist/internal';

import { REGEX, SOCKET_TOPICS, TRADING_TYPE } from 'src/common/constants';
import {
    TradeSocketResponseData,
    RecentTradeSocketResponseData,
    MargeDepthSocketResponse,
    SumMergedDepth,
} from 'src/types';

export interface TopicData {
    [symbol: string]: TradeSocketResponseData;
}
export interface TopicDataArr {
    [symbol: string]: TradeSocketResponseData[];
}
export interface TradeDataArr {
    [symbol: string]: RecentTradeSocketResponseData[];
}
interface IndexPrice {
    newPrice: string;
    prevPrice: string;
}
export interface SocketState {
    realtimes: TopicDataArr;
    broker: TopicData;
    slowBroker: TopicData;
    kline: TradeSocketResponseData;
    klineHistory: TradeSocketResponseData[];
    indexKlineHistory: TradeSocketResponseData[];
    indexKline: TradeSocketResponseData;
    mergedDepth: MargeDepthSocketResponse;
    sumMergedDepth: SumMergedDepth;
    trade: RecentTradeSocketResponseData[];
    indexPrice: IndexPrice;
    indexKlineHistoryUpdated: boolean;
    klineHistoryUpdated: boolean;
    klineResolution: string;
    indexKlineResolution: string;
}

const initialState: SocketState = {
    realtimes: {} as TopicDataArr,
    broker: {} as TopicData,
    slowBroker: {} as TopicData,
    kline: {} as TradeSocketResponseData,
    klineHistory: [] as TradeSocketResponseData[],
    indexKlineHistory: [] as TradeSocketResponseData[],
    indexKline: {} as TradeSocketResponseData,
    mergedDepth: {} as MargeDepthSocketResponse,
    sumMergedDepth: { totalAsk: 0, totalBid: 0, total: 0 } as SumMergedDepth,
    trade: [] as Array<RecentTradeSocketResponseData>,
    indexPrice: {} as IndexPrice,
    indexKlineHistoryUpdated: false,
    klineHistoryUpdated: false,
    klineResolution: '',
    indexKlineResolution: '',
};

//slice
export const socketSlice = createSlice({
    name: 'socketData',
    initialState,
    reducers: {
        updateTradeData: (state, action) => {
            const { responseData: payload, tradeType } = action.payload;
            const topic = payload.topic.split('_')[0];
            if (topic === SOCKET_TOPICS.SLOW_BROKER || topic === SOCKET_TOPICS.BROKER) {
                let parsedData = {};
                payload.data?.map((dateItem: TradeSocketResponseData) => {
                    if (
                        dateItem.s.match(REGEX.FUTURE_SYMBOL_ID) &&
                        tradeType === TRADING_TYPE.DERIVATIVES
                    ) {
                        parsedData = { ...parsedData, [dateItem.s as string]: dateItem };
                    } else if (
                        !dateItem.s.match(REGEX.FUTURE_SYMBOL_ID) &&
                        tradeType === TRADING_TYPE.SPOT
                    ) {
                        parsedData = { ...parsedData, [dateItem.s as string]: dateItem };
                    }
                });
                state[topic as 'broker'] = {
                    ...state[topic as 'broker'],
                    ...parsedData,
                };
            } else if (topic === SOCKET_TOPICS.TRADE) {
                let prevData = state[topic as 'trade'];
                // Make sure the recent trades should not increase then 60 records
                if (prevData.length > 59) {
                    prevData.length = 59;
                }
                state[topic as 'trade'] = [...payload.data.reverse(), ...prevData];
            } else if (topic === SOCKET_TOPICS.MERGED_DEPTH) {
                state[topic as 'mergedDepth'] = payload.data[0];
                const totalAsk = payload.data[0].a.reduce(
                    (total: number, ask: number[]) => total + Number(ask[1]),
                    0,
                );
                const totalBid = payload.data[0].b.reduce(
                    (total: number, bid: number[]) => total + Number(bid[1]),
                    0,
                );
                state.sumMergedDepth = {
                    totalAsk: totalAsk,
                    totalBid: totalBid,
                    total: totalAsk + totalBid,
                };
            } else if (topic === SOCKET_TOPICS.INDEX_KLINE) {
                const { data, f, params } = payload;
                state.indexPrice = {
                    prevPrice: state.indexPrice.newPrice,
                    newPrice: data[0].c,
                };
                state.indexKlineResolution = params.klineType;
                if (f) {
                    state.indexKlineHistory = data;
                    state.indexKlineHistoryUpdated = true;
                    state.indexPrice = {
                        prevPrice: '0.0',
                        newPrice: data[data.length - 1]?.c,
                    };
                    state[topic as 'indexKline'] = {} as TradeSocketResponseData;
                } else {
                    state[topic as 'indexKline'] = data[0];
                }
            } else if (topic === SOCKET_TOPICS.KLINE) {
                const { data, f, params } = payload;
                state.klineResolution = params.klineType;
                if (f) {
                    state.klineHistory = data;
                    state.klineHistoryUpdated = true;
                    state[topic as 'kline'] = {} as TradeSocketResponseData;
                } else {
                    state[topic as 'kline'] = data[0];
                }
            } else {
                const { symbol, data } = payload;
                const dataObj = state[topic as 'realtimes'];
                let prvData: WritableDraft<TradeSocketResponseData>[] = [];
                if (symbol in dataObj) {
                    prvData = dataObj[symbol];
                }
                state[topic as 'realtimes'][symbol as string] = [...prvData.slice(1), ...data];
            }
        },
        resetTradeData: (state) => {
            state.kline = {} as TradeSocketResponseData;
            state.klineHistory = [];
            state.indexKline = {} as TradeSocketResponseData;
            state.indexPrice = {} as IndexPrice;
            state.mergedDepth = {} as MargeDepthSocketResponse;
            state.trade = [];
            state.klineResolution = '';
            state.indexKlineResolution = '';
        },
        clearKlineData: (state, action) => {
            if (action.payload === 'INDEX') {
                state.indexKlineHistory = [];
                state.indexKlineHistoryUpdated = false;
            } else {
                state.klineHistory = [];
                state.klineHistoryUpdated = false;
            }
        },
    },
});

export const { clearKlineData, resetTradeData, updateTradeData } = socketSlice.actions;
